import { css, html, LitElement } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { htmlTitle } from "se-shared/directives/html-title.directive";
import { ToasterService } from "se-shared/services/toaster.service";
import { container } from "tsyringe";
import { LoginAuditLogModel, RunAuditLogModel, AuditLogOperation } from "../../models/audit-log";
import { AuditLogService } from "../../services/audit-log.service";
import { AuthService } from "../../services/auth.service";
import { ModalDialogService } from "../../services/modal-editor.service";
import { UserState } from "../../services/user.state";
import { DataGridColumn } from "../components/data-grid-template";
import { SeDataGrid } from "../components/data-grid.element";

@customElement("se-login-audit-log")
export class SeAuditLoginLogElement extends LitElement {
    @property() configId;

    private _authService: AuthService;
    private _modalService: ModalDialogService;
    private _auditLogService: AuditLogService;
    private _userState: UserState;
    private _toasterService: ToasterService;
    private _columns: DataGridColumn[] = [];
    @state() private _data: LoginAuditLogModel[];
    private _pageIndex = 1;
    private _recordsPerPage = 100;
    private _totalRecordCount: number;

    @state() private _isLoading = true;

    @query("se-data-grid") private _dataGrid: SeDataGrid;

    constructor() {
        super();
        this._toasterService = container.resolve(ToasterService);
        this._userState = container.resolve(UserState);
        this._modalService = container.resolve(ModalDialogService);
        this._auditLogService = container.resolve(AuditLogService);
        this._authService = container.resolve(AuthService);
    }

    connectedCallback() {
        super.connectedCallback();
        document.title = "Audit Logins";
        this._userState.selectedLabelId = -1;
        this._userState.selectedSpaceId = -5;
        this._userState.selectedSpaceOrLabelChanged.triggerVoid();
        this.loadData();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }

    private async loadData(isShowLoading = true) {
        this._columns = [
            { field: "loginName", title: "Login" },
            { field: "userName", title: "Name" },
            {
                field: "isSuccess",
                title: "Success",
                align: "center",
                checkbox: true,
                getValue: (row: LoginAuditLogModel) => !row.isFailure,
            },
            { field: "accessLevel", title: "Access Level" },
            { field: "ipAddress", title: "IP Address" },
            { field: "location", title: "Location" },
            { field: "description", title: "Description" },
            {
                field: "created",
                title: "Created",
                align: "center",
                sortable: true,
                template: (row) => {
                    const date = new Date(row.created);
                    return html`${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
                },
            },
        ];

        if (isShowLoading) this._isLoading = true;
        try {
            const beforeUtc = new Date();
            beforeUtc.setDate(beforeUtc.getDate() - 30);

            //this._data = [];
            //this._totalRecordCount = 0;

            const result = await this._auditLogService.api.getLoginLogAsync(
                this._authService.user.organizationId,
                beforeUtc.toUTCString(),
                new Date().toUTCString(),
                this._pageIndex,
                this._recordsPerPage
            );
            if (result.isOk) {
                this._data = result.value.auditLog;
                this._totalRecordCount = result.value.totalRecordCount;
            } else {
                this._toasterService.showUnexpectedError(result.err.message);
            }
        } finally {
            this._isLoading = false;
        }
    }

    private onPageChanged(evt: CustomEvent) {
        evt.stopPropagation();
        this._pageIndex = evt.detail.pageIndex;
        this._dataGrid.pageIndex = this._pageIndex;
        this.loadData();
    }

    private refresh() {
        this.loadData();
    }

    render() {
        return html`
            <div class="body">
                <div class="header">
                    <div class="left-header">
                        <se-secondary-button
                            @click=${this.refresh}
                            ${htmlTitle("Refresh")}
                            .customStyle=${{ padding: "1px 6px", margin: 0 }}
                            icon="far fa-redo"
                        ></se-secondary-button>
                    </div>
                </div>
                <se-data-grid
                    class="grid"
                    .rows=${this._data}
                    .recordsPerPage=${this._recordsPerPage}
                    .pageIndex=${this._pageIndex}
                    .columns=${this._columns}
                    placeholder="No data available."
                    .isLoading=${this._isLoading}
                ></se-data-grid>
                <se-pagination .dataType=${"Audit Logs"} .recordCount=${this._totalRecordCount} .recordsPerPage=${this._recordsPerPage} @pagechanged=${this.onPageChanged}></se-pagination>
            </div>
        `;
    }

    static styles = css`
        :host {
            display: block;
            box-sizing: border-box;
            font: var(--font);
            height: 100%;
        }
        .body {
            min-height: 220px;
            height: 100%;
            display: flex;
            flex-direction: column;
            gap: 5px;
        }
        .header {
            margin-left: 5px;
            display: flex;
            align-items: end;
            justify-content: space-between;
            overflow: hidden;
            padding-right: 5px;
            margin-right: -5px;
            padding-bottom: 5px;
            margin-bottom: -5px;
        }
        .left-header {
            display: flex;
            align-items: center;
            gap: 5px;
        }
        .grid {
            flex: 1;
        }
        .checkbox {
            width: 1rem;
            height: 1rem;
        }
        input[type="checkbox"]:checked {
            background-color: var(--color-secondary);
        }
        .label {
            background-color: dimgray;
            border-radius: 3px 3px;
            font: var(--font-smaller);
        }
    `;
}
